"use strict";

import gsap from 'gsap';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

function targetBlank() {
    // remove subdomain of current site's url and setup regex
    let internal = location.host.replace("www.", "");
    internal = new RegExp(internal, "i");

    let a = document.getElementsByTagName('a'); // then, grab every link on the page
    for (let i = 0; i < a.length; i++) {
        let href = a[i].host; // set the host of each link
        if ((!internal.test(href) && !/^javascript/.test(a[i].getAttribute("href"))) || /\.pdf$/.test(a[i].getAttribute("href"))) { // make sure the href doesn't contain current site's host
            a[i].setAttribute('target', '_blank'); // if it doesn't, set attributes
        }
    }
}

document.addEventListener('DOMContentLoaded', function () {

    targetBlank();
    
    // Accordion (both the boxed .accordion and the plain .accordion-plain
    // variant) — click the header, toggle aria-expanded. [hidden] still
    // does the actual show/hide (formgrad's technique — see _accordion.scss
    // for why a CSS-only grid-row collapse kept leaving a stray gap), but
    // GSAP now animates the height across that instead of an instant snap:
    // unhide → tween 0 → auto on open, tween → 0 → hide on close. GSAP
    // specifically (not a plain CSS transition) because it can tween height
    // to/from "auto" itself — plain CSS can't transition to a content-based
    // height at all. Not header.nextElementSibling: .accordion-plain has the
    // divider in between, so the panel is found by class within the shared
    // parent instead of assuming a sibling position.
    document.querySelectorAll('.accordion__header, .accordion-plain__header').forEach(function (header) {
        const panel = header.parentElement.querySelector('.accordion__panel, .accordion-plain__panel');
        if (!panel) return;

        header.addEventListener('click', function () {
            const expanded = header.getAttribute('aria-expanded') === 'true';
            header.setAttribute('aria-expanded', String(!expanded));

            if (expanded) {
                gsap.to(panel, {
                    height: 0,
                    duration: 0.3,
                    ease: 'power2.inOut',
                    onComplete: function () {
                        panel.hidden = true;
                    },
                });
            } else {
                panel.hidden = false;
                gsap.fromTo(panel, { height: 0 }, {
                    height: 'auto',
                    duration: 0.3,
                    ease: 'power2.inOut',
                });
            }
        });
    });

    // Navbar link pill — a single shared highlight (see .navbar__pill in
    // _navbar.scss) slides from the active link to whichever link is
    // hovered, instead of every item getting its own instant background.
    // Needs JS: CSS alone can't measure one sibling's position/width to
    // place a different element there.
    document.querySelectorAll('.navbar__links').forEach(function (list) {
        const pill = list.querySelector('.navbar__pill');
        const items = list.querySelectorAll(':scope > .nav-item, :scope > .nav-dropdown > .nav-item');
        if (!pill || !items.length) return;

        const activeItem = list.querySelector(':scope > .nav-item[aria-current="page"], :scope > .nav-dropdown > .nav-item[aria-current="page"]');

        function movePillTo(item) {
            const itemRect = item.getBoundingClientRect();
            const listRect = list.getBoundingClientRect();
            pill.style.transform = 'translateX(' + (itemRect.left - listRect.left) + 'px)';
            pill.style.width = itemRect.width + 'px';
            pill.style.opacity = '1';
        }

        if (activeItem) {
            // Placed once, instantly — without this it would visibly slide
            // in from the top-left corner on every page load.
            pill.style.transition = 'none';
            movePillTo(activeItem);
            requestAnimationFrame(function () {
                pill.style.transition = '';
            });
        }

        items.forEach(function (item) {
            item.addEventListener('mouseenter', function () {
                movePillTo(item);
            });
        });

        list.addEventListener('mouseleave', function () {
            if (activeItem) {
                movePillTo(activeItem);
            } else {
                pill.style.opacity = '0';
            }
        });

        window.addEventListener('resize', function () {
            const current = list.querySelector(':scope > .nav-item:hover, :scope > .nav-dropdown > .nav-item:hover') || activeItem;
            if (current) movePillTo(current);
        });
    });

    // Mobile navbar — hamburger toggles the mobile link list. One GSAP
    // timeline drives the whole interaction, reversed on close instead of
    // building a second set of animations:
    //  1. icon bars slide together, then rotate into an X
    //  2. the dropdown panel fades/slides into view, links staggered
    document.querySelectorAll('.navbar').forEach(function (navbar) {
        const toggleEl = navbar.querySelector('.btn-navbar');
        const menuEl = navbar.querySelector('.navbar__mobile-menu');
        const barTop = navbar.querySelector('.btn-navbar__bar--top');
        const barBottom = navbar.querySelector('.btn-navbar__bar--bottom');
        if (!toggleEl || !menuEl || !barTop || !barBottom) return;

        const menuItems = menuEl.querySelectorAll(':scope > *');
        let menuOpen = false;

        gsap.set(menuItems, { opacity: 0, y: -8 });

        const menuTl = gsap.timeline({
            paused: true,
            defaults: { ease: 'power3.inOut' },
            onReverseComplete: () => gsap.set(menuEl, { display: 'none' }),
        });

        menuTl
            .set(menuEl, { display: 'flex' })
            .to(barBottom, { y: -4, duration: 0.2, ease: 'power4.inOut' })
            .to(barTop, { y: 4, duration: 0.2, ease: 'power4.inOut' }, '<')
            .to(barBottom, { rotation: 45, transformOrigin: '50% 50%', duration: 0.3, ease: 'power4.inOut' })
            .to(barTop, { rotation: -45, transformOrigin: '50% 50%', duration: 0.3, ease: 'power4.inOut' }, '<')
            .to(menuItems, {
                opacity: 1,
                y: 0,
                stagger: 0.05,
                duration: 0.35,
                ease: 'power3.out',
            }, '-=0.15');

        toggleEl.addEventListener('click', function () {
            menuOpen = !menuOpen;
            toggleEl.setAttribute('aria-expanded', String(menuOpen));

            if (menuOpen) {
                menuTl.play();
            } else {
                menuTl.reverse();
            }
        });
    });

    // SwiperTeaser — row of teaser cards, partial next-card peek. The track
    // must start/end flush with .container's own CONTENT edge (where the
    // heading/nav actually sit), not its border-box edge — .swiper-teaser__header
    // carries the .container class itself, so its own padding-inline has to
    // be added back on top of getBoundingClientRect().left, otherwise the
    // slides overshoot the heading/buttons by exactly that padding on both
    // sides. CSS padding on the .swiper root itself doesn't constrain
    // Swiper's own scroll-bounds math — only slidesOffsetBefore/After do.
    document.querySelectorAll('.swiper-teaser .swiper').forEach(function (el) {
        const wrapper = el.closest('.swiper-teaser');
        const container = wrapper.querySelector('.swiper-teaser__header');

        function getOffset() {
            if (!container) return 0;
            const paddingLeft = parseFloat(getComputedStyle(container).paddingLeft) || 0;
            return Math.max(0, container.getBoundingClientRect().left + paddingLeft);
        }

        const swiper = new Swiper(el, {
            modules: [Navigation],
            slidesPerView: 'auto',
            spaceBetween: 8,
            slidesOffsetBefore: getOffset(),
            slidesOffsetAfter: getOffset(),
            navigation: {
                nextEl: wrapper.querySelector('.swiper__nav-btn--next'),
                prevEl: wrapper.querySelector('.swiper__nav-btn--prev'),
            },
        });

        let resizeTimeout;
        window.addEventListener('resize', function () {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(function () {
                const offset = getOffset();
                swiper.params.slidesOffsetBefore = offset;
                swiper.params.slidesOffsetAfter = offset;
                swiper.update();
            }, 100);
        });
    });

    // SwiperSimple — single large image per slide. Counter ("01 / 08")
    // follows the real (non-cloned) slide index since the gallery loops.
    document.querySelectorAll('.swiper-simple .swiper').forEach(function (el) {
        const wrapper = el.closest('.swiper-simple');
        const counterCurrent = wrapper.querySelector('.swiper__counter-current');

        function updateCounter(swiper) {
            if (counterCurrent) counterCurrent.textContent = String(swiper.realIndex + 1).padStart(2, '0');
        }

        new Swiper(el, {
            modules: [Navigation],
            loop: true,
            navigation: {
                nextEl: wrapper.querySelector('.swiper__nav-btn--next'),
                prevEl: wrapper.querySelector('.swiper__nav-btn--prev'),
            },
            on: {
                init: updateCounter,
                slideChange: updateCounter,
            },
        });
    });

});
